"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";
import { arrowGeometry, type ArrowAnchor } from "./sourcesChartModel";
import styles from "./SourcesChart.module.css";

export type SourcesArrow = {
  anchor: ArrowAnchor;
  /** Ancre utilisée sous 600 px ; `null` masque la flèche sur petit écran. */
  smallAnchor: ArrowAnchor | null;
  note?: ReactNode;
};

const SMALL = 600;

/**
 * Flèches courtes posées sur la tangente des courbes, avec leur mention.
 * Dessinées en pixels réels après mesure du graphique : dans le SVG étiré des
 * bandes, une pointe de flèche serait déformée. Décoratives, donc absentes du
 * rendu serveur et masquées aux lecteurs d'écran.
 */
export function SourcesArrows({ arrows }: { arrows: ReadonlyArray<SourcesArrow> }) {
  const ref = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<{ width: number; height: number } | null>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const measure = () => setSize({ width: element.clientWidth, height: element.clientHeight });
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const small = size ? size.width < SMALL : false;
  const drawn = size
    ? arrows.flatMap((arrow) => {
        const anchor = small ? arrow.smallAnchor : arrow.anchor;
        return anchor ? [{ geometry: arrowGeometry(anchor, size.width, size.height, small), note: arrow.note }] : [];
      })
    : [];

  return (
    <div ref={ref} className={styles.arrows} aria-hidden="true">
      {size ? (
        <svg viewBox={`0 0 ${size.width} ${size.height}`} className={styles.arrowsSvg}>
          {drawn.map(({ geometry }, index) => (
            <g key={index}>
              <path d={geometry.line} />
              <path d={geometry.head} />
            </g>
          ))}
        </svg>
      ) : null}
      {drawn.map(({ geometry, note }, index) =>
        note ? (
          <span key={index} className={styles.note} style={{ left: geometry.labelLeft, top: geometry.labelTop }}>
            {note}
          </span>
        ) : null,
      )}
    </div>
  );
}
